import { Editor } from '@tiptap/react'
import debounce from 'lodash/debounce'
import { useEffect, useState } from 'react'
import { useEditorElement } from './useEditorElement'

/**
 * Custom hook for TextEditor that provides a content overflow state.
 * Given an overflow height in pixels, the return value will be true
 * if the content in the editor exceeds that height.
 */
export const useContentOverflow = (
  editor: Editor | null,
  overflowHeight?: number | null
) => {
  // Track if content is overflowed, pass state to context
  const [isContentOverflowed, setContentOverflowed] = useState(false)
  const editorElement = useEditorElement(editor)

  useEffect(() => {
    if (!editorElement || overflowHeight == null) {
      return
    }

    const debouncedCallback = debounce<ResizeObserverCallback>((entries) => {
      for (const entry of entries) {
        // Round blockSize to nearest integer
        // 2024-06-24: Workaround for floating-point error in Edge on Windows
        const blockSize = Math.round(entry.borderBoxSize[0].blockSize)
        setContentOverflowed(blockSize >= overflowHeight)
      }
    }, 250)

    const resizeObserver = new ResizeObserver(debouncedCallback)
    resizeObserver.observe(editorElement)

    setContentOverflowed(
      editorElement.getBoundingClientRect().height >= overflowHeight
    )

    return () => {
      debouncedCallback.cancel()
      resizeObserver.disconnect()
    }
  }, [editorElement, overflowHeight])

  return [isContentOverflowed] as const
}
